import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import {
  collection, collectionGroup, query, where, onSnapshot, doc, getDoc, getDocs
} from 'firebase/firestore';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import {
  Headphones, BookOpen, Grid, List, Tablet, ArrowRight, ShoppingCart, Lock, Calendar
} from 'lucide-react';

/* ═══════════════════════════════════════════
   READER LIBRARY
   Purchased and granted-free audiobooks and ebooks.
═══════════════════════════════════════════ */

export default function ReaderLibrary({ currentReader }) {
  const uid = currentReader?.uid;

  const [audiobookPurchases, setAudiobookPurchases] = useState([]);
  const [ebookPurchases, setEbookPurchases] = useState([]);
  const [audiobookProgress, setAudiobookProgress] = useState({});
  const [ebookProgress, setEbookProgress] = useState({});
  const [audiobookMeta, setAudiobookMeta] = useState({});
  const [ebookMeta, setEbookMeta] = useState({});
  const [ebookPreOrders, setEbookPreOrders] = useState([]);
  const [audiobookPreOrders, setAudiobookPreOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  // Fetch pre-orders across all authors (collection group query on preOrders subcollection)
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;

    async function fetchPreOrders() {
      try {
        // We can't easily do collection group on preOrders since it's nested under both ebooks and audiobooks.
        // Instead, we'll search all ebooks and audiobooks that have pre-orders for this user.
        // This requires reading preOrders subcollections — but we need to know which books have them.
        // Simpler approach: query collectionGroup for all preOrders where readerUid matches.
        // Note: This requires a Firestore composite index on preOrders collection group.
        const ebookPOs = [];
        const audiobookPOs = [];

        // Try to query ebook preOrders
        try {
          const allEbooks = await getDocs(collectionGroup(db, 'ebooks'));
          for (const ebookDoc of allEbooks.docs) {
            if (!ebookDoc.data().preOrder?.preOrderEnabled) continue;
            const pathParts = ebookDoc.ref.path.split('/');
            const authorId = pathParts[1];
            const poSnap = await getDocs(
              query(
                collection(db, 'authors', authorId, 'ebooks', ebookDoc.id, 'preOrders'),
                where('readerUid', '==', uid),
                where('status', '==', 'authorised')
              )
            );
            poSnap.forEach(d => {
              ebookPOs.push({
                id: d.id,
                ...d.data(),
                ebookId: ebookDoc.id,
                authorId,
                bookTitle: ebookDoc.data().title,
                coverUrl: ebookDoc.data().coverUrl,
                authorName: '',
                releaseDate: ebookDoc.data().preOrder?.preOrderReleaseDate,
              });
            });
          }
        } catch (err) {
          console.error('Error fetching ebook pre-orders:', err);
        }

        // Try to query audiobook preOrders
        try {
          const allAudiobooks = await getDocs(collectionGroup(db, 'audiobooks'));
          for (const abDoc of allAudiobooks.docs) {
            if (!abDoc.data().preOrder?.preOrderEnabled) continue;
            const pathParts = abDoc.ref.path.split('/');
            const authorId = pathParts[1];
            const poSnap = await getDocs(
              query(
                collection(db, 'authors', authorId, 'audiobooks', abDoc.id, 'preOrders'),
                where('readerUid', '==', uid),
                where('status', '==', 'authorised')
              )
            );
            poSnap.forEach(d => {
              audiobookPOs.push({
                id: d.id,
                ...d.data(),
                audiobookId: abDoc.id,
                authorId,
                bookTitle: abDoc.data().title,
                coverUrl: abDoc.data().coverUrl,
                authorName: '',
                releaseDate: abDoc.data().preOrder?.preOrderReleaseDate,
              });
            });
          }
        } catch (err) {
          console.error('Error fetching audiobook pre-orders:', err);
        }

        if (!cancelled) {
          setEbookPreOrders(ebookPOs);
          setAudiobookPreOrders(audiobookPOs);
        }
      } catch (err) {
        console.error('Failed to fetch pre-orders:', err);
      }
    }

    fetchPreOrders();
    return () => { cancelled = true; };
  }, [uid]);

  // Subscribe to purchases
  useEffect(() => {
    if (!uid) return;
    const unsubs = [];

    // Audiobook purchases
    const abQ = query(collection(db, 'audiobookPurchases'), where('uid', '==', uid));
    unsubs.push(onSnapshot(abQ, snap => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setAudiobookPurchases(items);
    }, err => console.error('audiobookPurchases listener:', err)));

    // Ebook purchases
    const ebQ = query(collection(db, 'ebookPurchases'), where('uid', '==', uid));
    unsubs.push(onSnapshot(ebQ, snap => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setEbookPurchases(items);
    }, err => console.error('ebookPurchases listener:', err)));

    return () => unsubs.forEach(u => u());
  }, [uid]);

  // Fetch audiobook metadata (title, cover, author, chapterCount)
  // Falls back to purchase doc data if Firestore rules block the read
  // (e.g. when the author has set status to draft/scheduled)
  useEffect(() => {
    if (audiobookPurchases.length === 0) {
      setAudiobookMeta({});
      return;
    }
    let cancelled = false;

    async function fetchMeta() {
      const meta = {};
      for (const purchase of audiobookPurchases) {
        const { authorId, audiobookId } = purchase;
        if (!authorId || !audiobookId) continue;
        try {
          const snap = await getDoc(doc(db, 'authors', authorId, 'audiobooks', audiobookId));
          if (snap.exists()) {
            meta[audiobookId] = { id: snap.id, authorId, ...snap.data() };
          } else {
            // Doc may have been deleted — fall back to purchase data
            meta[audiobookId] = { id: audiobookId, authorId, title: purchase.title, authorName: purchase.authorName };
          }
        } catch {
          // Firestore rules may block read for draft/scheduled — use purchase data
          meta[audiobookId] = { id: audiobookId, authorId, title: purchase.title, authorName: purchase.authorName };
        }
      }
      if (!cancelled) setAudiobookMeta(meta);
    }

    fetchMeta();
    return () => { cancelled = true; };
  }, [audiobookPurchases]);

  // Fetch ebook metadata — same fallback pattern
  useEffect(() => {
    if (ebookPurchases.length === 0) {
      setEbookMeta({});
      return;
    }
    let cancelled = false;

    async function fetchMeta() {
      const meta = {};
      for (const purchase of ebookPurchases) {
        const { authorId, ebookId } = purchase;
        if (!authorId || !ebookId) continue;
        try {
          const snap = await getDoc(doc(db, 'authors', authorId, 'ebooks', ebookId));
          if (snap.exists()) {
            meta[ebookId] = { id: snap.id, authorId, ...snap.data() };
          } else {
            meta[ebookId] = { id: ebookId, authorId, title: purchase.title, authorName: purchase.authorName };
          }
        } catch {
          meta[ebookId] = { id: ebookId, authorId, title: purchase.title, authorName: purchase.authorName };
        }
      }
      if (!cancelled) setEbookMeta(meta);
    }

    fetchMeta();
    return () => { cancelled = true; };
  }, [ebookPurchases]);

  // Fetch progress docs for all purchased content
  useEffect(() => {
    if (!uid) return;
    const unsubs = [];

    // Audiobook progress: docs keyed as {uid}_{audiobookId}
    if (audiobookPurchases.length > 0) {
      for (const purchase of audiobookPurchases) {
        const progressDocId = `${uid}_${purchase.audiobookId}`;
        unsubs.push(onSnapshot(
          doc(db, 'audiobookProgress', progressDocId),
          snap => {
            if (snap.exists()) {
              setAudiobookProgress(prev => ({ ...prev, [purchase.audiobookId]: snap.data() }));
            }
          },
          () => {} // Ignore errors for missing progress docs
        ));
      }
    }

    // Ebook progress: docs keyed as {uid}_{ebookId}
    if (ebookPurchases.length > 0) {
      for (const purchase of ebookPurchases) {
        const progressDocId = `${uid}_${purchase.ebookId}`;
        unsubs.push(onSnapshot(
          doc(db, 'ebookProgress', progressDocId),
          snap => {
            if (snap.exists()) {
              setEbookProgress(prev => ({ ...prev, [purchase.ebookId]: snap.data() }));
            }
          },
          () => {}
        ));
      }
    }

    // Mark loading done after a tick (all listeners set up)
    const timer = setTimeout(() => setIsLoading(false), 500);

    return () => {
      unsubs.forEach(u => u());
      clearTimeout(timer);
    };
  }, [uid, audiobookPurchases, ebookPurchases]);

  // Show ALL purchased content regardless of status — readers who paid should always see their content
  const hasContent = audiobookPurchases.length > 0 || ebookPurchases.length > 0 || ebookPreOrders.length > 0 || audiobookPreOrders.length > 0;

  // Calculate audiobook progress percentage
  function getAudiobookPercent(audiobookId) {
    const progress = audiobookProgress[audiobookId];
    if (!progress) return 0;
    const meta = audiobookMeta[audiobookId];
    const totalChapters = meta?.chapterCount || 1;
    const completedChapters = progress.completedChapters || 0;
    const currentChapterPosition = progress.currentChapterPosition || 0; // 0-1 fraction
    return Math.min(100, Math.round(((completedChapters + currentChapterPosition) / totalChapters) * 100));
  }

  // Calculate ebook progress percentage
  function getEbookPercent(ebookId) {
    const progress = ebookProgress[ebookId];
    if (!progress) return 0;
    // CFI-based progress stored as a percentage (0-100)
    return Math.min(100, Math.round(progress.percent || 0));
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
      </div>
    );
  }

  // Completely empty state
  if (!hasContent && !isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">My Library</h2>
            <p className="text-sm text-slate-500">Your purchased and granted content</p>
          </div>
        </div>
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
              <BookOpen className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="mb-1 text-lg font-semibold text-slate-700">Your library is empty</h3>
            <p className="mb-4 max-w-sm text-sm text-slate-500">
              You haven't purchased or been granted any audiobooks or eBooks yet. Browse available titles to get started.
            </p>
            <Button
              onClick={() => window.dispatchEvent(new CustomEvent('navigate-reader', { detail: 'available-arcs' }))}
              className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700"
            >
              Browse Available Titles <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const gridClass = viewMode === 'grid'
    ? 'grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'
    : 'space-y-3';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">My Library</h2>
          <p className="text-sm text-slate-500">Your purchased and granted content</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
          <button
            onClick={() => setViewMode('grid')}
            className={`rounded-md p-1.5 transition-colors ${viewMode === 'grid' ? 'bg-teal-50 text-teal-700' : 'text-slate-400 hover:text-slate-600'}`}
            title="Grid view"
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`rounded-md p-1.5 transition-colors ${viewMode === 'list' ? 'bg-teal-50 text-teal-700' : 'text-slate-400 hover:text-slate-600'}`}
            title="List view"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Audiobooks section */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <Headphones className="h-5 w-5 text-teal-600" />
          <h3 className="text-lg font-semibold text-slate-800">Audiobooks</h3>
          {audiobookPurchases.length > 0 && (
            <Badge className="bg-slate-100 text-slate-600 border-slate-200">{audiobookPurchases.length}</Badge>
          )}
        </div>

        {audiobookPurchases.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center">
              <Headphones className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm text-slate-500">No audiobooks yet. Browse available titles below.</p>
            </CardContent>
          </Card>
        ) : (
          <div className={gridClass}>
            {audiobookPurchases.map(purchase => {
              const meta = audiobookMeta[purchase.audiobookId] || {};
              const percent = getAudiobookPercent(purchase.audiobookId);
              return viewMode === 'grid'
                ? <AudiobookCardGrid key={purchase.id} purchase={purchase} meta={meta} percent={percent} />
                : <AudiobookCardList key={purchase.id} purchase={purchase} meta={meta} percent={percent} />;
            })}
          </div>
        )}
      </section>

      {/* eBooks section */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-teal-600" />
          <h3 className="text-lg font-semibold text-slate-800">eBooks</h3>
          {ebookPurchases.length > 0 && (
            <Badge className="bg-slate-100 text-slate-600 border-slate-200">{ebookPurchases.length}</Badge>
          )}
        </div>

        {ebookPurchases.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center">
              <BookOpen className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm text-slate-500">No eBooks yet. Browse available titles below.</p>
            </CardContent>
          </Card>
        ) : (
          <div className={gridClass}>
            {ebookPurchases.map(purchase => {
              const meta = ebookMeta[purchase.ebookId] || {};
              const percent = getEbookPercent(purchase.ebookId);
              return viewMode === 'grid'
                ? <EbookCardGrid key={purchase.id} purchase={purchase} meta={meta} percent={percent} />
                : <EbookCardList key={purchase.id} purchase={purchase} meta={meta} percent={percent} />;
            })}
          </div>
        )}
      </section>

      {/* Pre-Ordered section */}
      {(ebookPreOrders.length > 0 || audiobookPreOrders.length > 0) && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-slate-800">Pre-Ordered</h3>
            <Badge className="bg-blue-50 text-blue-600 border-blue-200">
              {ebookPreOrders.length + audiobookPreOrders.length}
            </Badge>
          </div>
          <div className={gridClass}>
            {ebookPreOrders.map(po => (
              <PreOrderCard key={po.id} preOrder={po} type="ebook" viewMode={viewMode} />
            ))}
            {audiobookPreOrders.map(po => (
              <PreOrderCard key={po.id} preOrder={po} type="audiobook" viewMode={viewMode} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   AUDIOBOOK CARDS
───────────────────────────────────────── */

function AudiobookCardGrid({ purchase, meta, percent }) {
  return (
    <Card className="group overflow-hidden border-slate-200 transition-shadow hover:shadow-md">
      <div className="aspect-[3/4] bg-slate-100">
        {meta.coverUrl ? (
          <img src={meta.coverUrl} alt={meta.title || ''} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Headphones className="h-10 w-10 text-slate-300" />
          </div>
        )}
      </div>
      <CardContent className="space-y-2 p-3">
        <h4 className="line-clamp-2 text-sm font-semibold text-slate-800">{meta.title || purchase.title || 'Untitled'}</h4>
        <p className="text-xs text-slate-500">{meta.authorName || purchase.authorName || ''}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span>{percent}% complete</span>
            <span>{meta.chapterCount || 0} chapters</span>
          </div>
          <Progress value={percent} className="h-1.5" />
        </div>
        <Button
          size="sm"
          className="w-full gap-1.5 bg-teal-600 text-white hover:bg-teal-700"
          onClick={() => { window.location.href = `/listen/${purchase.audiobookId}`; }}
        >
          <Headphones className="h-3.5 w-3.5" /> Listen
        </Button>
      </CardContent>
    </Card>
  );
}

function AudiobookCardList({ purchase, meta, percent }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="flex items-center gap-4 p-3">
        <div className="h-16 w-12 flex-shrink-0 overflow-hidden rounded bg-slate-100">
          {meta.coverUrl ? (
            <img src={meta.coverUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Headphones className="h-5 w-5 text-slate-300" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-semibold text-slate-800">{meta.title || purchase.title || 'Untitled'}</h4>
          <p className="text-xs text-slate-500">{meta.authorName || purchase.authorName || ''}</p>
          <div className="mt-1 flex items-center gap-2">
            <Progress value={percent} className="h-1.5 flex-1" />
            <span className="text-[11px] text-slate-400 whitespace-nowrap">{percent}%</span>
          </div>
        </div>
        <Button
          size="sm"
          className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700"
          onClick={() => { window.location.href = `/listen/${purchase.audiobookId}`; }}
        >
          <Headphones className="h-3.5 w-3.5" /> Listen
        </Button>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────────────────
   EBOOK CARDS
───────────────────────────────────────── */

function EbookCardGrid({ purchase, meta, percent }) {
  return (
    <Card className="group overflow-hidden border-slate-200 transition-shadow hover:shadow-md">
      <div className="aspect-[3/4] bg-slate-100">
        {meta.coverUrl ? (
          <img src={meta.coverUrl} alt={meta.title || ''} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center">
            <BookOpen className="h-10 w-10 text-slate-300" />
          </div>
        )}
      </div>
      <CardContent className="space-y-2 p-3">
        <h4 className="line-clamp-2 text-sm font-semibold text-slate-800">{meta.title || purchase.title || 'Untitled'}</h4>
        <p className="text-xs text-slate-500">{meta.authorName || purchase.authorName || ''}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span>{percent}% read</span>
          </div>
          <Progress value={percent} className="h-1.5" />
        </div>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            className="flex-1 gap-1.5 bg-teal-600 text-white hover:bg-teal-700"
            onClick={() => { window.location.href = `/read/${purchase.ebookId}`; }}
          >
            <BookOpen className="h-3.5 w-3.5" /> Read
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-slate-200 px-2 text-slate-500 hover:border-teal-300 hover:text-teal-700"
            title="Send to Kindle or Kobo"
            onClick={() => { window.location.href = `/read/${purchase.ebookId}#send`; }}
          >
            <Tablet className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EbookCardList({ purchase, meta, percent }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="flex items-center gap-4 p-3">
        <div className="h-16 w-12 flex-shrink-0 overflow-hidden rounded bg-slate-100">
          {meta.coverUrl ? (
            <img src={meta.coverUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <BookOpen className="h-5 w-5 text-slate-300" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-semibold text-slate-800">{meta.title || purchase.title || 'Untitled'}</h4>
          <p className="text-xs text-slate-500">{meta.authorName || purchase.authorName || ''}</p>
          <div className="mt-1 flex items-center gap-2">
            <Progress value={percent} className="h-1.5 flex-1" />
            <span className="text-[11px] text-slate-400 whitespace-nowrap">{percent}%</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700"
            onClick={() => { window.location.href = `/read/${purchase.ebookId}`; }}
          >
            <BookOpen className="h-3.5 w-3.5" /> Read
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-slate-200 px-2 text-slate-500 hover:border-teal-300 hover:text-teal-700"
            title="Send to Kindle or Kobo"
            onClick={() => { window.location.href = `/read/${purchase.ebookId}#send`; }}
          >
            <Tablet className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────────────────
   PRE-ORDER CARD (locked state)
───────────────────────────────────────── */

function PreOrderCard({ preOrder, type, viewMode }) {
  const isEbook = type === 'ebook';
  const releaseDate = preOrder.releaseDate;
  const releaseDateStr = releaseDate
    ? (releaseDate.toDate ? releaseDate.toDate() : new Date(releaseDate)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  if (viewMode === 'list') {
    return (
      <Card className="border-blue-100">
        <CardContent className="flex items-center gap-4 p-3">
          <div className="relative h-16 w-12 flex-shrink-0 overflow-hidden rounded bg-slate-100">
            {preOrder.coverUrl ? (
              <img src={preOrder.coverUrl} alt="" className="h-full w-full object-cover opacity-60" />
            ) : (
              <div className="flex h-full items-center justify-center">
                {isEbook ? <BookOpen className="h-5 w-5 text-slate-300" /> : <Headphones className="h-5 w-5 text-slate-300" />}
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Lock className="h-4 w-4 text-white" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-sm font-semibold text-slate-800">{preOrder.bookTitle || 'Untitled'}</h4>
            {releaseDateStr && (
              <p className="flex items-center gap-1 text-[11px] text-blue-600">
                <Calendar className="h-3 w-3" /> Expected {releaseDateStr}
              </p>
            )}
          </div>
          <Badge className="bg-blue-50 text-blue-700 border-blue-200">Pre-Ordered</Badge>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="group overflow-hidden border-blue-100">
      <div className="relative aspect-[3/4] bg-slate-100">
        {preOrder.coverUrl ? (
          <img src={preOrder.coverUrl} alt={preOrder.bookTitle || ''} className="h-full w-full object-cover opacity-60" />
        ) : (
          <div className="flex h-full items-center justify-center">
            {isEbook ? <BookOpen className="h-10 w-10 text-slate-300" /> : <Headphones className="h-10 w-10 text-slate-300" />}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <Lock className="h-8 w-8 text-white/80" />
        </div>
        <Badge className="absolute right-2 top-2 bg-blue-600 text-white border-0 text-[10px] font-semibold">
          Pre-Ordered
        </Badge>
      </div>
      <CardContent className="space-y-2 p-3">
        <h4 className="line-clamp-2 text-sm font-semibold text-slate-800">{preOrder.bookTitle || 'Untitled'}</h4>
        {releaseDateStr && (
          <p className="flex items-center gap-1 text-[11px] text-blue-600">
            <Calendar className="h-3 w-3" /> Expected {releaseDateStr}
          </p>
        )}
        <Button
          size="sm"
          className="w-full gap-1.5 bg-blue-100 text-blue-700 cursor-default"
          disabled
        >
          <Lock className="h-3.5 w-3.5" /> Available on release
        </Button>
      </CardContent>
    </Card>
  );
}
